<template>
  <div
    class="board-snapshot"
    :style="{ width: boardSize + 'px', height: boardSize + 'px' }"
  >
    <!-- Simple static board representation -->
    <div class="static-board">
      <!-- Board squares -->
      <div class="board-squares">
        <div v-for="row in 8" :key="'row-' + row" class="board-row">
          <div
            v-for="col in 8"
            :key="'col-' + col"
            class="board-square"
            :class="{
              'light-square': (row + col) % 2 === 1,
              'dark-square': (row + col) % 2 === 0,
            }"
          ></div>
        </div>
      </div>

      <!-- Piece overlay -->
      <div v-if="currentFen" class="piece-overlay">
        <div
          v-for="(piece, index) in parsedPosition"
          :key="'piece-' + index"
          class="piece"
          :class="pieceClass(piece)"
          :style="pieceStyle(index)"
        ></div>

        <!-- Preview move indicator -->
        <div v-if="previewMove" class="move-indicator">
          <div
            class="move-square from-square"
            :style="moveSquareStyle(previewMove.from)"
          ></div>
          <div
            class="move-square to-square"
            :style="moveSquareStyle(previewMove.to)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PgnDataBuilder } from "vue-chess-shared/pgn/PgnDataBuilder";
import { CHESS_START_POSITION } from "vue-chess-shared";
import Chess from "chess.js";

export default {
  name: "BoardSnapshot",

  props: {
    fen: {
      type: String,
      default: "",
    },
    pgn: {
      type: String,
      default: "",
    },
    boardSize: {
      type: Number,
      default: 160,
    },
  },

  computed: {
    pgnData() {
      if (!this.pgn) {
        return null;
      }

      try {
        const builder = new PgnDataBuilder(this.pgn, new Chess());
        return builder.pgnData();
      } catch (error) {
        console.error("Error parsing PGN:", error);
        return null;
      }
    },

    previewMove() {
      if (!this.pgnData) {
        return null;
      }

      const move = this.pgnData.previewMove;
      if (!move) {
        return null;
      }

      // Extract from and to squares from the move
      return {
        from: move.from,
        to: move.to,
      };
    },

    currentFen() {
      if (this.fen) {
        return this.fen;
      }
      if (!this.pgn) {
        return CHESS_START_POSITION;
      }

      if (this.pgnData) {
        return this.pgnData.previewFen;
      }

      return CHESS_START_POSITION;
    },

    parsedPosition() {
      // Parse FEN string to get piece positions
      if (!this.currentFen) return [];

      const fenParts = this.currentFen.split(" ");
      const position = fenParts[0];
      const pieces = [];

      // FEN position is encoded from rank 8 to 1 (top to bottom)
      const ranks = position.split("/");

      ranks.forEach((rank, rankIndex) => {
        let fileIndex = 0;

        for (let i = 0; i < rank.length; i++) {
          const char = rank.charAt(i);

          if (/\d/.test(char)) {
            // If it's a number, skip that many squares
            fileIndex += parseInt(char, 10);
          } else {
            // It's a piece
            pieces.push({
              type: char,
              position: { rank: rankIndex, file: fileIndex },
            });
            fileIndex++;
          }
        }
      });

      return pieces;
    },

    squareSize() {
      return this.boardSize / 8;
    },
  },

  methods: {
    pieceClass(piece) {
      const color = piece.type === piece.type.toUpperCase() ? "white" : "black";
      const type = piece.type.toLowerCase();
      return `piece-${color}-${type}`;
    },

    pieceStyle(index) {
      const piece = this.parsedPosition[index];
      return {
        top: `${piece.position.rank * this.squareSize}px`,
        left: `${piece.position.file * this.squareSize}px`,
        width: `${this.squareSize}px`,
        height: `${this.squareSize}px`,
      };
    },

    moveSquareStyle(square) {
      if (!square) return {};

      // Convert algebraic notation (e.g., 'e4') to coordinates
      const file = square.charCodeAt(0) - "a".charCodeAt(0);
      const rank = 8 - parseInt(square.charAt(1), 10);

      return {
        top: `${rank * this.squareSize}px`,
        left: `${file * this.squareSize}px`,
        width: `${this.squareSize}px`,
        height: `${this.squareSize}px`,
      };
    },
  },
};
</script>

<style scoped>
.board-snapshot {
  display: inline-block;
  background-color: #fff;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
}

.static-board {
  width: 100%;
  height: 100%;
  position: relative;
}

.board-squares {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.board-row {
  display: flex;
  flex: 1;
}

.board-square {
  flex: 1;
}

.light-square {
  background-color: #f0d9b5;
}

.dark-square {
  background-color: #b58863;
}

.piece-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.piece {
  position: absolute;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* Piece images */
.piece-white-p {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wP.svg");
}
.piece-white-r {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wR.svg");
}
.piece-white-n {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wN.svg");
}
.piece-white-b {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wB.svg");
}
.piece-white-q {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wQ.svg");
}
.piece-white-k {
  background-image: url("https://lichess1.org/assets/piece/cburnett/wK.svg");
}

.piece-black-p {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bP.svg");
}
.piece-black-r {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bR.svg");
}
.piece-black-n {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bN.svg");
}
.piece-black-b {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bB.svg");
}
.piece-black-q {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bQ.svg");
}
.piece-black-k {
  background-image: url("https://lichess1.org/assets/piece/cburnett/bK.svg");
}

/* Move indicator styles */
.move-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.move-square {
  position: absolute;
  border: 2px solid;
  border-radius: 2px;
  opacity: 0.8;
  box-sizing: border-box;
}

.from-square {
  border-color: rgba(155, 199, 0, 0.8);
  background-color: rgba(155, 199, 0, 0.2);
}

.to-square {
  border-color: rgba(155, 199, 0, 0.8);
  background-color: rgba(155, 199, 0, 0.4);
}
</style>
